import React, { useState, useEffect } from "react";
import Layout from "../pages/Layout";
import Advertise from "../pages/Advertise";

const DaysSalesOutstanding = () => {
  // State variables for DSO calculation
  const [accountsReceivable, setAccountsReceivable] = useState(0); // Accounts receivable
  const [creditSales, setCreditSales] = useState(0); // Total credit sales
  const [days, setDays] = useState(30); // Number of days (default to 30)
  const [dso, setDso] = useState(0); // Calculated DSO

  // Event handlers to update state values
  const handleAccountsReceivableChange = (e) =>
    setAccountsReceivable(parseFloat(e.target.value) || 0);
  const handleCreditSalesChange = (e) =>
    setCreditSales(parseFloat(e.target.value) || 0);
  const handleDaysChange = (e) => setDays(parseInt(e.target.value) || 0);

  // useEffect hook to calculate DSO when any input changes
  useEffect(() => {
    if (creditSales > 0) {
      const calculatedDso = (accountsReceivable / creditSales) * days;
      setDso(calculatedDso);
    } else {
      setDso(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });
  }, [accountsReceivable, creditSales, days]);

  return (
    <Layout
      title="Days Sales Outstanding Calculator"
      path="days-sales-outstanding"
    >
      <section className="section-padding" id="dso-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* DSO Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">
                  Days Sales Outstanding (DSO) Calculator
                </h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Accounts Receivable Input */}
                    <div className="col-md-6 col-lg-4">
                      <label
                        htmlFor="accountsReceivable"
                        className="form-label"
                      >
                        Accounts Receivable (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Accounts Receivable:</strong> The amount of money owed by customers for goods or services provided on credit."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="accountsReceivable"
                        placeholder="Enter accounts receivable"
                        value={accountsReceivable || ""}
                        onChange={handleAccountsReceivableChange}
                      />
                    </div>

                    {/* Total Credit Sales Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="creditSales" className="form-label">
                        Total Credit Sales (₹)
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Total Credit Sales:</strong> The total value of sales made on credit during the specified period."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="creditSales"
                        placeholder="Enter total credit sales"
                        value={creditSales || ""}
                        onChange={handleCreditSalesChange}
                      />
                    </div>

                    {/* Number of Days Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="days" className="form-label">
                        Number of Days
                        <span
                          className="ms-2 text-muted"
                          data-bs-toggle="popover"
                          data-bs-html="true"
                          data-bs-placement="top"
                          data-bs-content="<strong>Number of Days:</strong> The period (in days) over which the calculation is performed."
                          style={{ cursor: "pointer" }}
                        >
                          <i className="bi bi-info-circle"></i>
                        </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="days"
                        placeholder="Enter number of days"
                        value={days || ""}
                        onChange={handleDaysChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Days Sales Outstanding (DSO):{" "}
                        <span className="fw-bold ms-3">
                          {dso === 0
                            ? "0.00"
                            : dso.toLocaleString(undefined, {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}{" "}
                          days
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Days Sales Outstanding (DSO) Calculator
                </h3>

                {/* What is a DSO Calculator? */}
                <h4 className="mt-3 mb-3">What is a DSO Calculator?</h4>
                <p>
                  A **Days Sales Outstanding (DSO) Calculator** helps you
                  measure the average number of days it takes a business to
                  collect payments after making a credit sale. It is a key
                  metric for assessing the efficiency of a company’s accounts
                  receivable processes.
                </p>

                {/* Why Use a DSO Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a DSO Calculator?</h4>
                <p>
                  Understanding your DSO helps manage cash flow effectively,
                  improve financial planning, and identify potential
                  inefficiencies in the payment collection process. High DSO
                  values can indicate delays in receiving payments, which may
                  strain cash flow.
                </p>

                {/* Benefits of Using a DSO Calculator */}
                <h4 className="mt-4 mb-3">
                  Benefits of Using a DSO Calculator
                </h4>
                <ul>
                  <li>
                    <strong>Indicates Efficiency:</strong> Helps evaluate the
                    efficiency of your accounts receivable processes.
                  </li>
                  <li>
                    <strong>Improves Cash Flow Management:</strong> Allows
                    businesses to monitor and manage cash inflows effectively.
                  </li>
                  <li>
                    <strong>Supports Financial Health Assessment:</strong> A low
                    DSO suggests quicker collections and healthier cash flow.
                  </li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a DSO Calculator?</h4>
                <p>This calculator is suitable for:</p>
                <ul>
                  <li>
                    <strong>Businesses with Credit Sales:</strong> Useful for
                    monitoring payment collection cycles.
                  </li>
                  <li>
                    <strong>Finance Teams:</strong> Helps in preparing financial
                    reports and improving cash flow forecasting.
                  </li>
                  <li>
                    <strong>Accounting Departments:</strong> Useful for
                    assessing the effectiveness of receivables management.
                  </li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">What is an ideal DSO?</h4>
                <p>
                  An ideal DSO varies by industry but is typically between 30
                  and 45 days. A lower DSO indicates efficient payment
                  collections, while a higher DSO may signal potential issues
                  with credit policies or payment delays.
                </p>

                <h4 className="mt-4 mb-3">How can I reduce my DSO?</h4>
                <p>
                  To reduce your DSO, consider implementing stricter credit
                  policies, offering early payment incentives, and improving
                  invoicing and follow-up processes.
                </p>

                <h4 className="mt-4 mb-3">
                  Why is DSO important for cash flow management?
                </h4>
                <p>
                  DSO directly impacts cash flow. The faster payments are
                  collected, the more liquidity is available to cover operating
                  expenses, invest in growth opportunities, or pay down debt.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default DaysSalesOutstanding;
